import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Star, CheckCircle2, XCircle, Sparkles } from "lucide-react";
import { API } from "@/App";

const StarRating = ({ value, onChange, size = 40 }) => {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-1 justify-center" onMouseLeave={() => setHover(0)}>
      {[1, 2, 3, 4, 5].map((n) => {
        const active = (hover || value) >= n;
        return (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            onMouseEnter={() => setHover(n)}
            className="focus:outline-none transition-transform hover:scale-110"
            data-testid={`star-${n}`}
            aria-label={`${n} estrella${n > 1 ? "s" : ""}`}
          >
            <Star
              className={`transition-colors ${active ? "text-amber-400 fill-amber-400" : "text-slate-300"}`}
              style={{ width: size, height: size }}
            />
          </button>
        );
      })}
    </div>
  );
};

const EncuestaCalidad = () => {
  const { id } = useParams();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [resolved, setResolved] = useState(null); // true/false/null
  const [responseRating, setResponseRating] = useState(0);
  const [attentionRating, setAttentionRating] = useState(0);
  const [comments, setComments] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/public/tickets/${id}/survey`)
      .then((r) => {
        setTicket(r.data);
        if (r.data.survey?.submitted_at) setDone(true);
      })
      .catch(() => toast.error("Encuesta no encontrada"))
      .finally(() => setLoading(false));
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (resolved === null) return toast.error("Indica si resolvimos tu solicitud");
    if (!responseRating) return toast.error("Califica el tiempo de respuesta");
    if (!attentionRating) return toast.error("Califica nuestra atención");

    setSending(true);
    try {
      await axios.post(`${API}/public/tickets/${id}/survey`, {
        resolved,
        response_rating: responseRating,
        attention_rating: attentionRating,
        comments,
      });
      setDone(true);
      toast.success("¡Gracias por tu opinión!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al enviar");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-400">
        Cargando encuesta...
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500">
        Encuesta no disponible
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 py-10"
      style={{ background: "linear-gradient(135deg,#00157f 0%,#0037ff 60%,#04ccda 100%)" }}
      data-testid="encuesta-page"
    >
      <div className="w-full max-w-lg">
        <div className="text-center mb-6 text-white">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white/20 backdrop-blur mb-3">
            <Sparkles className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'Mulish', sans-serif" }}>
            Encuesta de calidad
          </h1>
          <p className="text-sm text-cyan-100 mt-1">
            {ticket.folio} · {ticket.title}
          </p>
        </div>

        <Card className="shadow-2xl">
          <CardHeader>
            <CardTitle className="text-lg">
              {done ? "¡Gracias!" : "Tu opinión nos importa"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {done ? (
              <div className="py-10 text-center space-y-3" data-testid="survey-done">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100">
                  <CheckCircle2 className="w-9 h-9 text-green-600" />
                </div>
                <p className="text-slate-700 font-medium">Encuesta recibida correctamente.</p>
                <p className="text-sm text-slate-500">Tu retroalimentación nos ayuda a mejorar nuestro servicio.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-7" data-testid="survey-form">
                {/* Resolved */}
                <div>
                  <Label className="mb-3 block text-sm font-semibold text-slate-700">
                    ¿Resolvimos tu solicitud?
                  </Label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setResolved(true)}
                      className={`p-4 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${
                        resolved === true
                          ? "border-green-500 bg-green-50 text-green-700"
                          : "border-slate-200 hover:border-green-300"
                      }`}
                      data-testid="resolved-yes"
                    >
                      <CheckCircle2 className="w-8 h-8" />
                      <span className="font-semibold">Sí</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setResolved(false)}
                      className={`p-4 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${
                        resolved === false
                          ? "border-red-500 bg-red-50 text-red-700"
                          : "border-slate-200 hover:border-red-300"
                      }`}
                      data-testid="resolved-no"
                    >
                      <XCircle className="w-8 h-8" />
                      <span className="font-semibold">No</span>
                    </button>
                  </div>
                </div>

                {/* Response time */}
                <div>
                  <Label className="mb-3 block text-sm font-semibold text-slate-700 text-center">
                    El tiempo de respuesta fue adecuado
                  </Label>
                  <StarRating value={responseRating} onChange={setResponseRating} />
                  <p className="text-xs text-slate-400 text-center mt-2">
                    {responseRating ? `${responseRating}/5` : "Toca una estrella"}
                  </p>
                </div>

                {/* Attention */}
                <div>
                  <Label className="mb-3 block text-sm font-semibold text-slate-700 text-center">
                    ¿Cómo calificarías nuestra atención?
                  </Label>
                  <StarRating value={attentionRating} onChange={setAttentionRating} />
                  <p className="text-xs text-slate-400 text-center mt-2">
                    {attentionRating ? `${attentionRating}/5` : "Toca una estrella"}
                  </p>
                </div>

                {/* Comments */}
                <div>
                  <Label htmlFor="comments" className="mb-2 block text-sm font-semibold text-slate-700">
                    Comentarios (opcional)
                  </Label>
                  <Textarea
                    id="comments"
                    value={comments}
                    onChange={(e) => setComments(e.target.value)}
                    placeholder="Cuéntanos algo más..."
                    rows={4}
                    data-testid="survey-comments"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={sending}
                  className="w-full h-11 bg-blue-700 hover:bg-blue-800"
                  data-testid="survey-submit"
                >
                  {sending ? "Enviando..." : "Enviar encuesta"}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-xs text-cyan-200 mt-4">PD Travel — Gracias por tu tiempo</p>
      </div>
    </div>
  );
};

export default EncuestaCalidad;
